<template>
    <article>
        <v-tabs v-model="tab" height="40" slider-size="2" show-arrows class="mb-4">
            <v-tab :to="{hash: 'ce'}" tab-value="ce">Community Edition</v-tab>
            <v-tab :to="{hash: 'ee'}" tab-value="ee">Enterprise Edition</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
            <v-tab-item value="ce" eager transition="none">
                <h3>Step #1: Installing ClickHouse</h3>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo yum install -y yum-utils
sudo yum-config-manager --add-repo https://packages.clickhouse.com/rpm/clickhouse.repo
sudo yum install -y clickhouse-server clickhouse-client
sudo systemctl enable clickhouse-server
sudo systemctl start clickhouse-server
                    </pre>
                </CodeSnippet>

                <h3>Step #2: Installing Prometheus</h3>

                <p>
                    Coroot requires Prometheus with support for Remote Write Receiver, which has been available since v2.25.0.
                    Unfortunately, there is no official Prometheus RPM package. Here is a step-by-step guide on how to install Prometheus manually, including creating a systemd service file for it.
                </p>

                <p>Create a Prometheus user:</p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo useradd --no-create-home --shell /bin/false prometheus
                    </pre>
                </CodeSnippet>

                <p>
                    Navigate to the Prometheus <a href="https://prometheus.io/download/" target="_blank">download page</a>, and find the URL for the latest version of Prometheus for Linux and your architecture.
                    Use wget to download it:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
wget https://github.com/prometheus/prometheus/releases/download/v2.51.2/prometheus-2.51.2.linux-amd64.tar.gz
                    </pre>
                </CodeSnippet>

                <p>
                    Extract the downloaded tarball and move the binary to <var>/usr/local/bin/</var>:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo yum -y install tar
tar xvfz prometheus-2.51.2.linux-amd64.tar.gz
sudo cp prometheus-2.51.2.linux-amd64/prometheus /usr/local/bin/
                    </pre>
                </CodeSnippet>

                <p>
                    Create directories for Prometheus configuration files and data:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo mkdir /etc/prometheus
sudo mkdir /var/lib/prometheus
sudo chown -R prometheus:prometheus /etc/prometheus /var/lib/prometheus
                    </pre>
                </CodeSnippet>

                <p>
                    Create a Prometheus configuration file:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo cat &lt;&lt;EOF >/etc/prometheus/prometheus.yml
global:
  scrape_interval: 15s
EOF
                    </pre>
                </CodeSnippet>

                <p>
                    Create a systemd service file for Prometheus:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo cat &lt;&lt;EOF >/etc/systemd/system/prometheus.service
[Unit]
Description=Prometheus
After=network.target

[Service]
User=prometheus
Group=prometheus
Type=simple
ExecStart=/usr/local/bin/prometheus --config.file /etc/prometheus/prometheus.yml --storage.tsdb.path /var/lib/prometheus --enable-feature=remote-write-receiver

[Install]
WantedBy=multi-user.target
EOF
                    </pre>
                </CodeSnippet>

                <p>
                    Reload systemd to load the new service and start Prometheus:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo systemctl daemon-reload
sudo systemctl start prometheus
sudo systemctl enable prometheus
                    </pre>
                </CodeSnippet>

                <h3>Step #3: Installing Coroot</h3>

                <CodeSnippet>
                    <pre v-hl.sh>
curl -sfL https://raw.githubusercontent.com/coroot/coroot/main/deploy/install.sh | \
    BOOTSTRAP_PROMETHEUS_URL="http://127.0.0.1:9090" \
    BOOTSTRAP_REFRESH_INTERVAL=15s \
    BOOTSTRAP_CLICKHOUSE_ADDRESS=127.0.0.1:9000 \
    sh -
                    </pre>
                </CodeSnippet>

                <h3>Step #4: Installing coroot-node-agent</h3>

                <CodeSnippet>
                    <pre v-hl.sh>
curl -sfL https://raw.githubusercontent.com/coroot/coroot-node-agent/main/install.sh | \
    COLLECTOR_ENDPOINT=http://127.0.0.1:8080 \
    SCRAPE_INTERVAL=15s \
    sh -
                    </pre>
                </CodeSnippet>

                <h3>Step #5: Accessing Coroot</h3>

                <p>
                    Access Coroot at: http://&lt;node_ip&gt;:8080.
                </p>


                <h2>Uninstall Coroot</h2>

                <p>To uninstall Coroot run the following command:</p>

                <CodeSnippet>
                        <pre v-hl.bash>
/usr/bin/coroot-uninstall.sh
                        </pre>
                </CodeSnippet>

                <p>
                    Uninstall coroot-node-agent:
                </p>

                <CodeSnippet>
                        <pre v-hl.bash>
/usr/bin/coroot-node-agent-uninstall.sh
                        </pre>
                </CodeSnippet>


            </v-tab-item>
            <v-tab-item value="ee" eager transition="none">
                <v-alert color="info" outlined text class="mt-5">
                    The Enterprise Edition is a paid subscription (from $1 per CPU core/month) that offers extra features and priority support.
                    <a href="https://coroot.com/account" target="_blank" class="font-weight-bold">Start</a> your free trial today.
                </v-alert>

                <h3>Step #1: Installing ClickHouse</h3>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo yum install -y yum-utils
sudo yum-config-manager --add-repo https://packages.clickhouse.com/rpm/clickhouse.repo
sudo yum install -y clickhouse-server clickhouse-client
sudo systemctl enable clickhouse-server
sudo systemctl start clickhouse-server
                    </pre>
                </CodeSnippet>

                <h3>Step #2: Installing Prometheus</h3>

                <p>
                    Coroot requires Prometheus with support for Remote Write Receiver, which has been available since v2.25.0.
                    Unfortunately, there is no official Prometheus RPM package. Here is a step-by-step guide on how to install Prometheus manually, including creating a systemd service file for it.
                </p>

                <p>Create a Prometheus user:</p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo useradd --no-create-home --shell /bin/false prometheus
                    </pre>
                </CodeSnippet>

                <p>
                    Navigate to the Prometheus <a href="https://prometheus.io/download/" target="_blank">download page</a>, and find the URL for the latest version of Prometheus for Linux and your architecture.
                    Use wget to download it:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
wget https://github.com/prometheus/prometheus/releases/download/v2.51.2/prometheus-2.51.2.linux-amd64.tar.gz
                    </pre>
                </CodeSnippet>

                <p>
                    Extract the downloaded tarball and move the binary to <var>/usr/local/bin/</var>:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo yum -y install tar
tar xvfz prometheus-2.51.2.linux-amd64.tar.gz
sudo cp prometheus-2.51.2.linux-amd64/prometheus /usr/local/bin/
                    </pre>
                </CodeSnippet>

                <p>
                    Create directories for Prometheus configuration files and data:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo mkdir /etc/prometheus
sudo mkdir /var/lib/prometheus
sudo chown -R prometheus:prometheus /etc/prometheus /var/lib/prometheus
                    </pre>
                </CodeSnippet>

                <p>
                    Create a Prometheus configuration file:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo cat &lt;&lt;EOF >/etc/prometheus/prometheus.yml
global:
  scrape_interval: 15s
EOF
                    </pre>
                </CodeSnippet>

                <p>
                    Create a systemd service file for Prometheus:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo cat &lt;&lt;EOF >/etc/systemd/system/prometheus.service
[Unit]
Description=Prometheus
After=network.target

[Service]
User=prometheus
Group=prometheus
Type=simple
ExecStart=/usr/local/bin/prometheus --config.file /etc/prometheus/prometheus.yml --storage.tsdb.path /var/lib/prometheus --enable-feature=remote-write-receiver

[Install]
WantedBy=multi-user.target
EOF
                    </pre>
                </CodeSnippet>

                <p>
                    Reload systemd to load the new service and start Prometheus:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo systemctl daemon-reload
sudo systemctl start prometheus
sudo systemctl enable prometheus
                    </pre>
                </CodeSnippet>

                <h3>Step #3: Installing Coroot</h3>

                <v-alert color="info" outlined text class="mt-5">
                    To install Coroot Enterprise Edition, you'll need a license (from $1 per CPU core/month).
                    <a href="https://coroot.com/account" target="_blank" class="font-weight-bold">Start</a> your free trial today.
                </v-alert>

                <div class="subtitle-1">License key:</div>
                <v-form>
                    <v-text-field
                        v-model="license_key"
                        placeholder="COROOT-11111111-1111-1111-1111-111111111111"
                        outlined
                        dense
                    />
                </v-form>

                <CodeSnippet>
                    <pre class="hl">
curl -sfL https://raw.githubusercontent.com/coroot/coroot-ee/main/deploy/install.sh | \
    LICENSE_KEY="{{license_key || 'COROOT-LICENSE-KEY-HERE'}}" \
    BOOTSTRAP_PROMETHEUS_URL="http://127.0.0.1:9090" \
    BOOTSTRAP_REFRESH_INTERVAL=15s \
    BOOTSTRAP_CLICKHOUSE_ADDRESS=127.0.0.1:9000 \
    sh -</pre>
                </CodeSnippet>

                <h3>Step #5: Accessing Coroot</h3>

                <p>
                    Access Coroot at: http://&lt;node_ip&gt;:8080.
                </p>


                <h2>Uninstall Coroot</h2>

                <p>To uninstall Coroot run the following command:</p>

                <CodeSnippet>
                        <pre v-hl.bash>
/usr/bin/coroot-uninstall.sh
                        </pre>
                </CodeSnippet>

                <p>
                    Uninstall coroot-node-agent:
                </p>

                <CodeSnippet>
                        <pre v-hl.bash>
/usr/bin/coroot-node-agent-uninstall.sh
                        </pre>
                </CodeSnippet>


            </v-tab-item>
        </v-tabs-items>
    </article>
</template>

<script>

export default {
    data() {
        return {
            license_key: '',
            tab: null,
        }
    },
    computed: {},
}
</script>

<style scoped>
</style>
